// Espelho de ils/app/frontend/lib/trackers/hubspot/session.ts — manter em
// sincronia.
//
// O `hubspotutk` identifica o navegador, não a pessoa: ele é associado ao
// primeiro contato que fizer identify e o HubSpot nunca re-associa um utk que
// já tem dono. Um navegador que troca de conta fica preso ao contato antigo —
// os eventos ainda caem no contato certo (o e-mail tem precedência no beacon),
// mas a entrega de CTAs/web interactives resolve o contato só pelo utk e passa
// a avaliar a segmentação da pessoa errada.
//
// Este módulo dá ao HubSpot o "logout" que ele não tem: depois de cada
// identify gravamos um marcador (hash do e-mail + utk abençoado) e, no boot,
// se o usuário logado não bate com o marcador — ou o utk nunca foi validado —
// expiramos os cookies do HubSpot para que um utk virgem nasça amarrado ao
// usuário atual no próximo identify + page view.
//
// Tudo aqui é fail-open: qualquer erro deixa o tracking seguir como está.

const MARKER_COOKIE = '_qc_hubspot_identity';
const MARKER_DAYS = 365;
const HUBSPOT_COOKIES = ['hubspotutk', '__hstc', '__hssc', '__hssrc'];
const EXPIRED = 'Thu, 01 Jan 1970 00:00:00 GMT';

// Mesmo mapeamento do CookieManager: cookie de domínio para o marcador valer
// em qualquer app de *.qconcursos.com. Fora de staging/production, host-only.
const COOKIE_DOMAINS = {
  staging: 'qconcursos-hmg.com',
  production: 'qconcursos.com'
};

// Mesmos seletores do intercom_manager, que resolve o mesmo problema para o
// Intercom: no logout, zera a sessão HubSpot para a navegação anônima seguinte
// não acumular no contato recém-deslogado.
const LOGOUT_SELECTORS = [
  'a[href*="destroy_user_session"]',
  'a[href*="/conta/sair"]',
  '[data-js-event="logout"]',
  'form[action*="destroy_user_session"]',
  'form[action*="/conta/sair"]'
].join(', ');

let logoutBound = false;

function env() {
  return window.RAILS_ENV;
}

function cookieDomain() {
  return COOKIE_DOMAINS[env()];
}

function getCookie(name) {
  const prefix = `${name}=`;
  const match = document.cookie
    .split(';')
    .map(cookie => cookie.trim())
    .find(cookie => cookie.indexOf(prefix) === 0);

  return match ? match.substring(prefix.length) : null;
}

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  const domain = cookieDomain();
  const suffix = domain ? `;domain=.${domain}` : '';

  document.cookie = `${name}=${value};path=/;expires=${expires};SameSite=Lax${suffix}`;
}

function expireCookie(name) {
  // Expira nas duas formas: o cookie pode ser host-only ou de domínio,
  // dependendo de como o script do HubSpot o gravou.
  document.cookie = `${name}=;path=/;expires=${EXPIRED}`;

  const domain = cookieDomain();

  if (domain) {
    document.cookie = `${name}=;path=/;expires=${EXPIRED};domain=.${domain}`;
  }
}

// FNV-1a 32 bits. O marcador é um cookie de domínio e viaja em todo request —
// não pode carregar o e-mail em claro. Só precisamos de igualdade, não de
// segredo.
function fingerprint(value) {
  let hash = 0x811c9dc5;

  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }

  return (hash >>> 0).toString(16);
}

function identityFor(email, utk) {
  return `${fingerprint(email.trim().toLowerCase())}.${utk}`;
}

export function resetIdentitySession() {
  try {
    [...HUBSPOT_COOKIES, MARKER_COOKIE].forEach(expireCookie);
  } catch {
    // fail open
  }
}

export function syncIdentitySession(email) {
  try {
    if (env() === 'development' || !email) {
      return;
    }

    const utk = getCookie('hubspotutk');

    // Navegador virgem (ou tracker bloqueado por adblock): nada a resetar; o
    // utk que nascer já será do usuário atual.
    if (!utk) {
      return;
    }

    if (getCookie(MARKER_COOKIE) === identityFor(email, utk)) {
      return;
    }

    // Marcador ausente (bootstrap pós-deploy), e-mail trocou ou o utk mudou
    // por fora (outra aba, expiração natural): não dá para confiar que esse
    // utk pertence ao usuário logado.
    resetIdentitySession();
  } catch {
    // fail open
  }
}

export function persistIdentitySession(email) {
  try {
    if (env() === 'development' || !email) {
      return;
    }

    const utk = getCookie('hubspotutk');

    if (!utk) {
      return;
    }

    setCookie(MARKER_COOKIE, identityFor(email, utk), MARKER_DAYS);
  } catch {
    // fail open
  }
}

export function bindLogoutCleanup() {
  try {
    if (logoutBound) {
      return;
    }

    logoutBound = true;

    document.addEventListener(
      'click',
      event => {
        const target = event.target;

        if (target && target.closest && target.closest(LOGOUT_SELECTORS)) {
          resetIdentitySession();
        }
      },
      true
    );
  } catch {
    // fail open
  }
}
